import { useRef, useState } from 'react';
import { Check, CheckSquare, MoreVertical, Router as RouterIcon, RotateCcw, Video, Server, Square } from 'lucide-react';
import { Popover } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import type { RouterCheckKind, RouterCheckResponse, RouterHostDesignation } from '@/api/zoneRouter';
import { ConnectivityCheckButtons } from './ConnectivityCheckButtons';

/**
 * Three-dots overflow on a DiscoveredPeerList row.
 *
 * Houses every "non-primary" host action so the row itself stays a
 * scannable identity strip (ip · mac · hostname) with one explicit
 * primary CTA (the import icon). Inside the popover:
 *
 *   1. Selection toggle (enters bulk-select mode for the list).
 *   2. Designate-host buttons — "Это камера / NVR / роутер".
 *   3. Inline connectivity checks (ping / tcp / rtsp).
 *
 * Designed as a generic primitive that both Group B (router host) and
 * Group D (LC peer) consume — the kinds prop wires the visible check
 * buttons + the designate options the parent allows.
 */

export interface HostDesignationMenuProps {
  /** Stable id surfaced through callbacks. The MAC works for both
   *  router probe rows and LC discovery rows. */
  hostKey: string;
  /** What overrides the parent is willing to accept — different surfaces
   *  curate different sets (e.g. Router-card hides "router" because the
   *  card is itself the router; LC card exposes all three). */
  allowedDesignations: readonly RouterHostDesignation[];
  /** Connectivity-check kinds to expose. Empty → no check section. */
  checkKinds: readonly RouterCheckKind[];
  /** Whether bulk-select mode is currently on for the parent list.
   *  Drives the label of the first menu item. */
  bulkActive: boolean;
  /** True when the row is already selected in bulk mode. */
  bulkSelected: boolean;
  /** Visual size of the trigger (default small fits inside an icon strip). */
  size?: 'sm' | 'md';
  /** Optional disabled state — e.g. an already-imported row that
   *  doesn't need the menu. */
  disabled?: boolean;
  /** Current operator-set designation, if any. When set, the
   *  corresponding menu item paints a checkmark + "Сбросить" appears at
   *  the bottom. Finding #14 from the 2026-06-02 test report — without
   *  this the menu hides the host's current state and there's no undo.
   */
  currentDesignation?: RouterHostDesignation | null;

  /** Called when the operator picks "Select" / unselects the row. */
  onToggleBulk: () => void;
  /** Called when one of the designate items is chosen. The parent
   *  pipes this through to the BE designate-host endpoint. */
  onDesignate: (d: RouterHostDesignation) => void;
  /** Called when the operator picks "Сбросить" — clears the persisted
   *  designation so the BE's hostname heuristic takes over again.
   *  Optional: surfaces only when `currentDesignation` is set AND the
   *  parent supplies a handler (e.g. router source has the
   *  DELETE designate-host/ endpoint; future LC source might not). */
  onResetDesignation?: () => void;
  /** Async runner for the connectivity-check sub-component. */
  onCheck: (kind: RouterCheckKind) => Promise<RouterCheckResponse>;
}

export function HostDesignationMenu({
  hostKey,
  allowedDesignations,
  checkKinds,
  bulkActive,
  bulkSelected,
  size = 'sm',
  disabled = false,
  currentDesignation = null,
  onToggleBulk,
  onDesignate,
  onResetDesignation,
  onCheck,
}: HostDesignationMenuProps) {
  const { t } = useT();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const dim = size === 'sm' ? 'size-7' : 'size-8';
  const icon = size === 'sm' ? 'size-3.5' : 'size-4';

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center rounded-md text-muted-foreground transition-colors',
          'hover:bg-muted hover:text-foreground',
          'disabled:cursor-not-allowed disabled:opacity-40',
          dim,
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('equipment.discoveredPeer.menu.label')}
        data-testid={`peer-menu-${hostKey}`}
      >
        <MoreVertical className={icon} />
      </button>
      <Popover open={open} onOpenChange={setOpen} triggerRef={ref} side="bottom" align="end">
        <div className="min-w-[14rem] py-1" role="menu">
          {/* Select toggle */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onToggleBulk(); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted hover:text-foreground"
            data-testid={`peer-menu-${hostKey}-select`}
          >
            {bulkActive && bulkSelected ? (
              <CheckSquare className="size-3.5 text-primary" />
            ) : (
              <Square className="size-3.5 text-muted-foreground" />
            )}
            {bulkActive
              ? bulkSelected
                ? t('equipment.discoveredPeer.bulkUnselect')
                : t('equipment.discoveredPeer.bulkSelect')
              : t('equipment.discoveredPeer.bulkSelect')}
          </button>

          {/* Designate group — the menu item matching `currentDesignation`
              renders with a leading checkmark (finding #14) so the
              operator can see at a glance which kind the host is set
              to without having to read tiny chip text on the row. */}
          {allowedDesignations.length > 0 && (
            <>
              <MenuGroupLabel>{t('equipment.discoveredPeer.menu.designateGroup')}</MenuGroupLabel>
              {allowedDesignations.includes('camera') && (
                <DesignateItem
                  hostKey={hostKey}
                  designation="camera"
                  icon={<Video className="size-3.5" />}
                  label={t('equipment.discoveredPeer.designate.camera')}
                  active={currentDesignation === 'camera'}
                  onClick={() => { setOpen(false); onDesignate('camera'); }}
                />
              )}
              {allowedDesignations.includes('nvr') && (
                <DesignateItem
                  hostKey={hostKey}
                  designation="nvr"
                  icon={<Server className="size-3.5" />}
                  label={t('equipment.discoveredPeer.designate.nvr')}
                  active={currentDesignation === 'nvr'}
                  onClick={() => { setOpen(false); onDesignate('nvr'); }}
                />
              )}
              {allowedDesignations.includes('router') && (
                <DesignateItem
                  hostKey={hostKey}
                  designation="router"
                  icon={<RouterIcon className="size-3.5" />}
                  label={t('equipment.discoveredPeer.designate.router')}
                  active={currentDesignation === 'router'}
                  onClick={() => { setOpen(false); onDesignate('router'); }}
                />
              )}
              {/* Reset/undo — only surfaces when there's something to
                  reset AND the parent wired the handler. Clears the
                  persisted designation so the next probe falls back to
                  the BE's hostname heuristic. */}
              {currentDesignation && onResetDesignation && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setOpen(false); onResetDesignation(); }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  data-testid={`peer-menu-${hostKey}-designate-reset`}
                >
                  <RotateCcw className="size-3.5 opacity-70" />
                  {t('equipment.discoveredPeer.designate.reset')}
                </button>
              )}
            </>
          )}

          {/* Connectivity checks — laid out as inline pills inside the
              popover so the operator can run multiple without the menu
              re-opening between rounds. When the parent suppresses the
              check section by passing an empty `checkKinds` array AND
              wires the noChecksHint surface, we render an inline hint
              instead (finding #21 + connectivity-check semantics shift
              — hosts without a forward can't run the router-side check
              meaningfully, so we direct the operator to create a
              forward first). */}
          {checkKinds.length > 0 ? (
            <>
              <MenuGroupLabel>{t('equipment.discoveredPeer.menu.checksGroup')}</MenuGroupLabel>
              <div className="px-3 py-1.5">
                <ConnectivityCheckButtons
                  kinds={checkKinds}
                  layout="inline"
                  onCheck={onCheck}
                />
              </div>
            </>
          ) : (
            <>
              <MenuGroupLabel>{t('equipment.discoveredPeer.menu.checksGroup')}</MenuGroupLabel>
              <div
                className="px-3 py-1.5 text-[11px] italic text-muted-foreground"
                data-testid={`peer-menu-${hostKey}-no-forward-hint`}
              >
                {t('equipment.discoveredPeer.check.noForwardHint')}
              </div>
            </>
          )}
        </div>
      </Popover>
    </>
  );
}

function MenuGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-1 border-t border-border px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/80">
      {children}
    </div>
  );
}

function DesignateItem({
  hostKey,
  designation,
  icon,
  label,
  active = false,
  onClick,
}: {
  hostKey: string;
  designation: RouterHostDesignation;
  icon: React.ReactNode;
  label: string;
  /** When true, the row paints a leading check + a subtle accent bg so
   *  the current state is unmistakable. */
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      aria-checked={active || undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted hover:text-foreground',
        active && 'bg-primary-muted/40 text-foreground',
      )}
      data-testid={`peer-menu-${hostKey}-designate-${designation}`}
      data-active={active || undefined}
    >
      <span className={cn(active ? 'text-[color:var(--color-success)]' : 'text-muted-foreground')}>
        {active ? <Check className="size-3.5" aria-hidden /> : icon}
      </span>
      {label}
      {active && (
        <span className="ml-auto text-[10px] uppercase tracking-[0.08em] text-muted-foreground/80">
          ✓
        </span>
      )}
    </button>
  );
}

export default HostDesignationMenu;
